import { create } from 'zustand';
import { failureWords } from '@/shared/failure/failure';
import { toast } from './toastStore';

/**
 * A question asked before an action is applied, from anywhere in the app. The
 * request of 5 October 2026 (third), section 2: every action by the accountant,
 * the admin and the receptionist shows a confirmation first, in the shared
 * confirmation.
 *
 * A one-tap action has no state to hold a sheet in and no place to draw one, a
 * row of a table least of all. So the question is put here, in a store outside
 * React as the toasts are, and `ConfirmHost` draws it once for the whole app.
 * The command is handed over with the question and the host runs it: the sheet
 * stays up with its spinner until the command settles, so it cannot be sent
 * twice, and a refusal is said in a toast with the server's words.
 */
export interface ConfirmRequest {
  title: string;
  /** What will happen, as a plain question naming the thing: "Release the hold on JO-2047?". */
  statement: string;
  confirmLabel: string;
  cancelLabel: string;
  /** The command. The sheet stays busy until this promise settles. */
  run: () => Promise<unknown>;
}

/** The question on screen, numbered so one asked later is never taken for it. */
export interface OpenConfirmRequest extends ConfirmRequest {
  id: number;
}

interface ConfirmState {
  /** The one question on screen, or null. */
  request: OpenConfirmRequest | null;
  /** The command of that question is running. */
  busy: boolean;
  /**
   * Puts a question on screen. One at a time: while another is open, answered
   * or not, this one is not asked and the answer is false. A question never
   * changes under the finger about to answer it.
   */
  ask: (request: ConfirmRequest) => boolean;
  /** Yes: runs the command, once, and closes the question when it settles. */
  accept: () => Promise<void>;
  /**
   * Closes the question. Unanswered, its command never runs. Closed while the
   * command is running (the sheet's own close button, a swipe), the command
   * carries on without the sheet and a refusal is still said.
   */
  cancel: () => void;
  /**
   * Signing out, and between tests: the question goes, and a command still
   * running settles without a word, because whoever holds the device next is
   * not told about the last person's work.
   */
  reset: () => void;
}

let lastId = 0;
/** Moves on with every reset, so a command begun before it knows it is not to report. */
let epoch = 0;

/**
 * The words of a refusal: the server's, or plain ones for anything that is not
 * a sentence written for a person. Every confirmed action reports through
 * here, so this is where most failures are worded. See shared/failure.
 */
async function refusalOf(run: () => Promise<unknown>): Promise<string | null> {
  try {
    await run();
    return null;
  } catch (error) {
    return failureWords(error);
  }
}

/** Runs a command and says its refusal in a toast, unless the person has signed out since. */
async function runAndReport(run: () => Promise<unknown>): Promise<void> {
  const began = epoch;
  const refusal = await refusalOf(run);
  if (refusal !== null && began === epoch) toast(refusal, { tone: 'error' });
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  busy: false,
  ask: (request) => {
    if (get().request !== null) return false;
    lastId += 1;
    set({ request: { ...request, id: lastId }, busy: false });
    return true;
  },
  accept: async () => {
    const { request, busy } = get();
    if (request === null || busy) return;
    set({ busy: true });
    await runAndReport(request.run);
    // Closed while it ran, and perhaps another question asked since: that one is not this one's to close.
    if (get().request?.id === request.id) set({ request: null, busy: false });
  },
  cancel: () => {
    if (get().request !== null) set({ request: null, busy: false });
  },
  reset: () => {
    epoch += 1;
    set({ request: null, busy: false });
  },
}));

/**
 * Runs a command at once, unasked, for somebody who is not asked first. Its
 * refusal is said the same way as one that was confirmed.
 */
export function runUnasked(run: () => Promise<unknown>): void {
  void runAndReport(run);
}
